// Lightweight "kid profile" stored entirely in localStorage. No backend, no
// account. Captured in the optional <BornInPicker /> modal that appears once
// after the age selector.
//
// Keys:
//   we_kid_name           — child's first name (string, optional)
//   we_birth_country_code — ISO-like country code from /data/countries.js
//   we_heritage_codes     — JSON array of up to 3 heritage country codes
//   we_born_in_seen       — "1" once the picker has been shown (skip / submit)
//   we_first_visit_day    — integer day-of-year captured on first save; used
//                           by Home to show the birth country as Country of
//                           the Day on day 1 only.

const NAME_KEY = "we_kid_name";
const COUNTRY_KEY = "we_birth_country_code";
const HERITAGE_KEY = "we_heritage_codes";
const SEEN_KEY = "we_born_in_seen";
const FIRST_DAY_KEY = "we_first_visit_day";

const safeGet = (k) => {
  try { return localStorage.getItem(k); } catch { return null; }
};
const safeSet = (k, v) => {
  try { localStorage.setItem(k, v); } catch { /* quota — ignore */ }
};

export function getKidName() {
  const v = safeGet(NAME_KEY);
  return v && v.trim() ? v.trim() : "";
}

/** Bundle of everything the tier-home layouts need in one object — helps
 * child components keep their imports tight (they only care about `name`
 * and optionally the birth country). */
export function getKidProfile() {
  return {
    name: getKidName(),
    birthCountry: safeGet(COUNTRY_KEY) || "",
  };
}

export function setKidName(name) {
  const clean = String(name || "").trim().slice(0, 40);
  if (clean) safeSet(NAME_KEY, clean);
}

export function getBirthCountryCode() {
  return safeGet(COUNTRY_KEY) || "";
}

export function setBirthCountryCode(code) {
  if (code) safeSet(COUNTRY_KEY, String(code));
}

/**
 * Heritage countries (RETENTION-01). Up to 3 codes representing family
 * roots. Stored as a JSON array. Get/set are defensive so a stale or
 * malformed value can never brick the app.
 */
export function getHeritageCodes() {
  try {
    const raw = safeGet(HERITAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter(Boolean).slice(0, 3) : [];
  } catch { return []; }
}

export function setHeritageCodes(codes) {
  try {
    const clean = (Array.isArray(codes) ? codes : []).filter(Boolean).slice(0, 3);
    safeSet(HERITAGE_KEY, JSON.stringify(clean));
  } catch { /* quota — ignore */ }
}

export function getBornInSeen() {
  return safeGet(SEEN_KEY) === "1";
}

export function setBornInSeen() {
  safeSet(SEEN_KEY, "1");
}

// Day-of-year as an integer (Home uses this for Country of the Day rotation).
function dayOfYear() {
  const now = Date.now();
  const start = new Date(new Date().getFullYear(), 0, 0).getTime();
  return Math.floor((now - start) / 86400000);
}

export function getFirstVisitDay() {
  const v = safeGet(FIRST_DAY_KEY);
  return v == null ? null : Number(v);
}

export function ensureFirstVisitDay() {
  if (safeGet(FIRST_DAY_KEY) == null) {
    safeSet(FIRST_DAY_KEY, String(dayOfYear()));
  }
}

// True only on the kid's *first day* of using the app. We use this to swap
// in their birth country as the Country of the Day so the very first session
// feels personal ("Today's country is your country!").
export function isFirstVisitDay() {
  const first = getFirstVisitDay();
  return first != null && first === dayOfYear();
}
